import { useState } from 'react';
import { ArrowUpLeft, ArrowUpRight, CornerRightUp } from 'lucide-react';
import type { CornerData } from '@/types/telemetry';
import type { CornerMetric } from './CornerMetricTabs';
import { useI18n } from '@/context/I18nContext';
import { Pagination } from '@/components/common/Pagination';

interface AllCornersOverviewProps {
  corners: CornerData[];
  selectedCornerIndex: number;
  onSelectCorner: (index: number) => void;
  activeMetric?: CornerMetric;
}

const PAGE_SIZE = 10;

export function AllCornersOverview({ corners, selectedCornerIndex, onSelectCorner, activeMetric = 'speed' }: AllCornersOverviewProps) {
  const { t } = useI18n();
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(corners.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedCorners = corners.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="card p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5 pb-4 border-b border-border-subtle">
        <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
          {t('corner.allCornersOverview')}
        </span>
        <span className="text-xs text-text-muted mono">{corners.length} {t('corner.turn')}s</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="divider">
              <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.turn')}</th>
              {activeMetric === 'speed' && (
                <>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.entrance')} ({t('corner.avgMax')})</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.apex')} ({t('corner.avgMax')})</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.exit')} ({t('corner.avgMax')})</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.speedGain')}</th>
                </>
              )}
              {activeMetric === 'rpm' && (
                <>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.entrance')} ({t('corner.avgMax')})</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.apex')} ({t('corner.avgMax')})</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.exit')} ({t('corner.avgMax')})</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.rpmGain')}</th>
                </>
              )}
              {activeMetric === 'duration' && (
                <>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.bestDuration')}</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.avgDuration')}</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.worstDuration')}</th>
                  <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('corner.spread')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedCorners.map((c, idx) => {
              const actualIndex = (currentPage - 1) * PAGE_SIZE + idx;
              const turnNum = actualIndex + 1;
              const isSelected = actualIndex === selectedCornerIndex;
              const isLeft = c.direction === 'left';
              const isRight = c.direction === 'right';

              // Speed stats
              const entryAvg = c.entryStats?.avgSpeedKmh ?? 0;
              const entryMax = c.entryStats?.maxSpeedKmh ?? 0;
              const apexAvg = c.apexStats?.avgSpeedKmh ?? 0;
              const apexMax = c.apexStats?.maxSpeedKmh ?? 0;
              const exitAvg = c.exitStats?.avgSpeedKmh ?? 0;
              const exitMax = c.exitStats?.maxSpeedKmh ?? 0;
              const speedGain = c.speedGainKmh ?? 0;

              // RPM stats
              const entryRpmAvg = c.entryRpmStats?.avgRpm ?? 0;
              const entryRpmMax = c.entryRpmStats?.maxRpm ?? 0;
              const apexRpmAvg = c.apexRpmStats?.avgRpm ?? 0;
              const apexRpmMax = c.apexRpmStats?.maxRpm ?? 0;
              const exitRpmAvg = c.exitRpmStats?.avgRpm ?? 0;
              const exitRpmMax = c.exitRpmStats?.maxRpm ?? 0;
              const rpmGain = c.rpmGain ?? 0;

              // Duration stats
              const dur = c.durationStats;
              const bestDur = dur ? (dur.minDurationMs / 1000).toFixed(3) : '—';
              const avgDur = dur ? (dur.avgDurationMs / 1000).toFixed(3) : '—';
              const worstDur = dur ? (dur.maxDurationMs / 1000).toFixed(3) : '—';
              const spread = dur ? `±${(dur.stdDevMs / 1000).toFixed(3)}s` : '—';

              return (
                <tr
                  key={c.cornerId || actualIndex}
                  onClick={() => onSelectCorner(actualIndex)}
                  className={`hover:bg-bg-hover cursor-pointer transition-colors ${
                    idx < paginatedCorners.length - 1 ? 'divider' : ''
                  } ${
                    isSelected ? 'bg-accent/10 border-l-2 border-l-accent' : ''
                  }`}
                >
                  <td className="py-2.5 px-4 font-medium text-text-primary">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full border inline-flex items-center justify-center text-[11px] font-bold mono shrink-0 ${
                        isSelected ? 'bg-accent text-white border-accent' : 'bg-bg-surface text-text-secondary border-border-subtle'
                      }`}>
                        {turnNum}
                      </span>
                      <div className="flex items-center gap-1">
                        {isLeft && <ArrowUpLeft size={14} className="text-green" />}
                        {isRight && <ArrowUpRight size={14} className="text-red" />}
                        {!isLeft && !isRight && <CornerRightUp size={14} className="text-text-muted" />}
                        <span>{t('corner.turn')} {turnNum}</span>
                      </div>
                    </div>
                  </td>

                  {/* Speed columns */}
                  {activeMetric === 'speed' && (
                    <>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        <span>{entryAvg.toFixed(1)}</span>
                        <span className="text-text-muted text-xs ml-1.5">/ {entryMax.toFixed(1)}</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        <span>{apexAvg.toFixed(1)}</span>
                        <span className="text-text-muted text-xs ml-1.5">/ {apexMax.toFixed(1)}</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        <span>{exitAvg.toFixed(1)}</span>
                        <span className="text-text-muted text-xs ml-1.5">/ {exitMax.toFixed(1)}</span>
                      </td>
                      <td className={`py-2.5 px-4 text-right mono font-semibold ${speedGain >= 0 ? 'text-green' : 'text-red'}`}>
                        {speedGain >= 0 ? `+${speedGain.toFixed(1)}` : speedGain.toFixed(1)} <span className="text-[11px] font-normal text-text-muted">km/h</span>
                      </td>
                    </>
                  )}

                  {/* RPM columns */}
                  {activeMetric === 'rpm' && (
                    <>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        <span>{Math.round(entryRpmAvg).toLocaleString()}</span>
                        <span className="text-text-muted text-xs ml-1.5">/ {Math.round(entryRpmMax).toLocaleString()}</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        <span>{Math.round(apexRpmAvg).toLocaleString()}</span>
                        <span className="text-text-muted text-xs ml-1.5">/ {Math.round(apexRpmMax).toLocaleString()}</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        <span>{Math.round(exitRpmAvg).toLocaleString()}</span>
                        <span className="text-text-muted text-xs ml-1.5">/ {Math.round(exitRpmMax).toLocaleString()}</span>
                      </td>
                      <td className={`py-2.5 px-4 text-right mono font-semibold ${rpmGain >= 0 ? 'text-green' : 'text-red'}`}>
                        {rpmGain >= 0 ? `+${Math.round(rpmGain).toLocaleString()}` : Math.round(rpmGain).toLocaleString()} <span className="text-[11px] font-normal text-text-muted">RPM</span>
                      </td>
                    </>
                  )}

                  {/* Duration columns */}
                  {activeMetric === 'duration' && (
                    <>
                      <td className="py-2.5 px-4 text-right mono font-semibold text-green">
                        {bestDur} <span className="text-[11px] font-normal text-text-muted">s</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                        {avgDur} <span className="text-[11px] font-normal text-text-muted">s</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono font-medium text-text-muted">
                        {worstDur} <span className="text-[11px] font-normal text-text-muted">s</span>
                      </td>
                      <td className="py-2.5 px-4 text-right mono text-xs text-accent">
                        {spread}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 pb-4">
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
